import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { ImagePair } from '@/data/galleryData';

interface Project {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  sort_order: number;
  published: boolean;
  layout_type: string;
}

export function useProjectsBySection(sectionSlug: string, publishedOnly = false, userId?: string | null) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // If userId is explicitly passed as null (not undefined), wait for it
    if (userId === null) return;

    let query = supabase
      .from('projects')
      .select('*')
      .eq('section_slug', sectionSlug)
      .order('sort_order');

    if (userId) {
      query = query.eq('user_id', userId);
    }

    if (publishedOnly) {
      query = query.eq('published', true);
    }

    query.then(({ data }) => {
      setProjects((data || []) as Project[]);
      setLoading(false);
    });
  }, [sectionSlug, publishedOnly, userId]);

  return { projects, loading };
}

export function useProjectImages(projectId: string | undefined) {
  const [pairs, setPairs] = useState<ImagePair[]>([]);
  const [loading, setLoading] = useState(true);
  const prevProjectId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!projectId) {
      if (prevProjectId.current === undefined) {
        // Don't setLoading(false) yet — wait for a real projectId
      }
      return;
    }

    prevProjectId.current = projectId;
    setLoading(true);
    supabase
      .from('project_images')
      .select('*')
      .eq('project_id', projectId)
      .order('sort_order')
      .then(({ data }) => {
        setPairs(
          (data || []).map((img: any) => ({
            left: img.left_url || '',
            right: img.right_url || '',
            title: img.title || undefined,
            video_url: img.video_url || undefined,
            video_display_mode: img.video_display_mode || undefined,
          })),
        );
        setLoading(false);
      });
  }, [projectId]);

  return { pairs, loading };
}

export function useHomeImages(userId?: string | null) {
  const [pairs, setPairs] = useState<ImagePair[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId === null) return;

    let query = supabase
      .from('home_images')
      .select('*')
      .order('sort_order');

    if (userId) {
      query = query.eq('user_id', userId);
    }

    query.then(({ data }) => {
      setPairs(
        (data || []).map((img) => ({
          left: img.left_url,
          right: img.right_url,
          title: (img as any).title || undefined,
          video_url: (img as any).video_url || undefined,
          video_display_mode: (img as any).video_display_mode || undefined,
        }))
      );
      setLoading(false);
    });
  }, [userId]);

  return { pairs, loading };
}
